import { createContext, ReactNode, useContext } from 'react'
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form'

interface ModalWizardTabsContextProps<TDefaultValues> {
  data?: TDefaultValues
}

// Define the context type
interface ModalWizardTabsContextType<TDefaultValues> {
  data?: TDefaultValues
  useFormWizzard: UseFormReturn<FieldValues, unknown, FieldValues>
}
const ModalWizardTabsContext = createContext<
  ModalWizardTabsContextType<unknown> | undefined
>(undefined)

export const ModalWizardTabsProvider = <TDefaultValues,>({
  data,
  children,
}: ModalWizardTabsContextProps<TDefaultValues> & { children: ReactNode }) => {
  const useFormWizzard = useForm({
    defaultValues: data || {},
    mode: 'onChange',
  })

  const contextValue: ModalWizardTabsContextType<TDefaultValues> = {
    useFormWizzard,
    data: data,
  }

  return (
    <ModalWizardTabsContext.Provider value={contextValue}>
      {children}
    </ModalWizardTabsContext.Provider>
  )
}

// Custom hook to use the modal wizard tabs context
export const useModalWizardTabsContext = <TDefaultValues,>() => {
  const context = useContext(ModalWizardTabsContext) as
    | ModalWizardTabsContextType<TDefaultValues>
    | undefined
  if (context === undefined) {
    throw new Error(
      'useModalWizardTabsContext must be used within a ModalWizardTabsProvider'
    )
  }
  return context
}
